import React, { useState, useMemo, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Tabs,
  Tab,
  useTheme,
  Alert,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Stack,
  LinearProgress,
  Tooltip,
  alpha,
  Skeleton
} from '@mui/material';
import {
  Search as SearchIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Clear as ClearIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDistance } from 'date-fns';
import { AuthContext } from '../auth/AuthProvider';
import { useSuccessData } from './hooks/useSuccessData';
import { ClientSuccessMetric } from './services/successDataService';
import LoadingContainer from '../../components/common/LoadingContainer';
import CustomLoader from '../../components/common/CustomLoader';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const MotionCard = motion(Card);
const MotionBox = motion(Box);

// Helper function to reformat names
function formatName(name: string | undefined): string {
  if (!name) return '';
  if (name.includes(',')) {
    const parts = name.split(',');
    return `${parts[1].trim()} ${parts[0].trim()}`;
  }
  return name;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`success-tabpanel-${index}`}
      aria-labelledby={`success-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const SuccessOverviewOptimized: React.FC = () => {
  const { user, userMetadata } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  
  const {
    isLoading,
    error,
    recentUpdates,
    overdueUpdates,
    refresh,
    statistics
  } = useSuccessData();

  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [coachFilter, setCoachFilter] = useState('');
  const [achievementFilter, setAchievementFilter] = useState<string>('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Extract unique locations and coaches
  const { locations, coaches } = useMemo(() => {
    const locationSet = new Set<string>();
    const coachMap = new Map<string, string>();

    [...recentUpdates, ...overdueUpdates].forEach(metric => {
      if (metric.primary_location) locationSet.add(metric.primary_location);
      if (metric.coach_name) coachMap.set(metric.coach_name, metric.coach_name);
    });

    return {
      locations: Array.from(locationSet).sort(),
      coaches: Array.from(coachMap.values()).sort()
    };
  }, [recentUpdates, overdueUpdates]);

  // Filter data based on search and filters
  const filterData = (data: typeof recentUpdates) => {
    let filtered = [...data];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(metric =>
        formatName(metric.client_name).toLowerCase().includes(term) ||
        metric.client_id.toLowerCase().includes(term)
      );
    }

    if (locationFilter) {
      filtered = filtered.filter(metric => metric.primary_location === locationFilter);
    }

    if (coachFilter) {
      filtered = filtered.filter(metric => metric.coach_name === coachFilter);
    }

    if (achievementFilter) {
      filtered = filtered.filter(metric => {
        const totalAchieved = [
          metric.goal_achieved,
          metric.target1_achieved,
          metric.target2_achieved,
          metric.target3_achieved
        ].filter(Boolean).length;

        if (achievementFilter === 'all') return totalAchieved === 4;
        if (achievementFilter === 'partial') return totalAchieved > 0 && totalAchieved < 4;
        if (achievementFilter === 'none') return totalAchieved === 0;
        return true;
      });
    }

    return filtered;
  };

  const filteredRecentUpdates = useMemo(() => filterData(recentUpdates), [recentUpdates, searchTerm, locationFilter, coachFilter, achievementFilter]);
  const filteredOverdueUpdates = useMemo(() => filterData(overdueUpdates), [overdueUpdates, searchTerm, locationFilter, coachFilter, achievementFilter]);

  // Split overdue updates into two categories
  const { awaitingFirstUpdate, overdueWithHistory } = useMemo(() => {
    const awaiting = filteredOverdueUpdates.filter(item => item.awaiting_first_update === true);
    const withHistory = filteredOverdueUpdates.filter(item => !item.awaiting_first_update);
    return { awaitingFirstUpdate: awaiting, overdueWithHistory: withHistory };
  }, [filteredOverdueUpdates]);

  const activeFilters = [searchTerm, locationFilter, coachFilter, achievementFilter].filter(Boolean).length;

  const clearFilters = () => {
    setSearchTerm('');
    setLocationFilter('');
    setCoachFilter('');
    setAchievementFilter('');
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refresh();
    setIsRefreshing(false);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleRowClick = (clientId: string) => {
    navigate(`/clients/${clientId}`);
  };

  // Calculate achievement progress
  const calculateAchievementProgress = (metric: any) => {
    const total = 4; // goal + 3 targets
    const achieved = [
      metric.goal_achieved,
      metric.target1_achieved,
      metric.target2_achieved,
      metric.target3_achieved
    ].filter(Boolean).length;
    return (achieved / total) * 100;
  };

  // Statistics cards
  const renderStatistics = () => {
    const goalAchievementRate = statistics.totalClientsWithData > 0
      ? Math.round((statistics.clientsWithAtLeastOneGoal / statistics.totalClientsWithData) * 100)
      : 0;

    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <MotionCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            elevation={0}
            sx={{ 
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
              borderRadius: 3
            }}
          >
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Goals (30 days)
                  </Typography>
                  <Typography variant="h4" fontWeight={700}>
                    {statistics.goalsAchievedLast30Days}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    achieved
                  </Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 40, color: theme.palette.primary.main, opacity: 0.3 }} />
              </Stack>
            </CardContent>
          </MotionCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MotionCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            elevation={0}
            sx={{ 
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
              borderRadius: 3
            }}
          >
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Targets (30 days)
                  </Typography>
                  <Typography variant="h4" fontWeight={700}>
                    {statistics.targetsAchievedLast30Days}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    achieved
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 40, color: theme.palette.success.main, opacity: 0.3 }} />
              </Stack>
            </CardContent>
          </MotionCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MotionCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            elevation={0}
            sx={{ 
              background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)`,
              borderRadius: 3
            }}
          >
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Goal Achievement
                  </Typography>
                  <Typography variant="h4" fontWeight={700}>
                    {goalAchievementRate}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {statistics.clientsWithAtLeastOneGoal}/{statistics.totalClientsWithData} clients
                  </Typography>
                </Box>
                <AssessmentIcon sx={{ fontSize: 40, color: theme.palette.info.main, opacity: 0.3 }} />
              </Stack>
            </CardContent>
          </MotionCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <MotionCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            elevation={0}
            sx={{ 
              background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.05)} 100%)`,
              borderRadius: 3
            }}
          >
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography color="text.secondary" variant="body2" gutterBottom>
                    Overdue Updates
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color="error">
                    {overdueUpdates.length}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    6+ weeks old
                  </Typography>
                </Box>
                <WarningIcon sx={{ fontSize: 40, color: theme.palette.error.main, opacity: 0.3 }} />
              </Stack>
            </CardContent>
          </MotionCard>
        </Grid>
      </Grid>
    );
  };

  // DataGrid columns for awaiting first update
  const columnsForFirstUpdate: GridColDef[] = [
    {
      field: 'client_name',
      headerName: 'Client',
      flex: 1.5,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams) => (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <Stack spacing={0.5}>
            <Typography variant="body2" fontWeight={600}>
              {formatName(params.value as string)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {params.row.client_id}
            </Typography>
          </Stack>
        </motion.div>
      ),
    },
    {
      field: 'coach_name',
      headerName: 'Coach',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon fontSize="small" color="action" />
          <Typography variant="body2">
            {formatName(params.value as string)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'first_session_date',
      headerName: 'Started',
      flex: 1.2,
      minWidth: 160,
      renderCell: (params: GridRenderCellParams) => {
        const date = params.value as Date;
        return (
          <Stack>
            <Typography variant="body2" color="warning.main" fontWeight={600}>
              {date.toLocaleDateString()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDistance(date, new Date(), { addSuffix: true })}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: 'primary_location',
      headerName: 'Location',
      flex: 1,
      minWidth: 140,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocationIcon fontSize="small" color="action" />
          <Typography variant="body2">
            {params.value as string}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
          <IconButton
            size="small"
            onClick={() => handleRowClick(params.row.client_id)}
            sx={{
              bgcolor: alpha(theme.palette.warning.main, 0.1),
              '&:hover': {
                bgcolor: alpha(theme.palette.warning.main, 0.2),
              },
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </motion.div>
      ),
    },
  ];

  // DataGrid columns for overdue updates with history
  const columns: GridColDef[] = [
    {
      field: 'client_name',
      headerName: 'Client',
      flex: 1.5,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams) => (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <Stack spacing={0.5}>
            <Typography variant="body2" fontWeight={600}>
              {formatName(params.value as string)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {params.row.client_id}
            </Typography>
            {params.row.awaiting_first_update && (
              <Chip
                label="First update required"
                size="small"
                color="error"
                variant="outlined"
                sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600, alignSelf: 'flex-start' }}
              />
            )}
          </Stack>
        </motion.div>
      ),
    },
    {
      field: 'coach_name',
      headerName: 'Coach',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon fontSize="small" color="action" />
          <Typography variant="body2">
            {formatName(params.value as string)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'updated_at',
      headerName: 'Update Date',
      flex: 1,
      minWidth: 140,
      renderCell: (params: GridRenderCellParams) => {
        const date = params.value as Date;
        const metric = params.row as ClientSuccessMetric & { awaiting_first_update?: boolean; first_session_date?: Date };
        const isAwaiting = metric.awaiting_first_update;
        const firstSessionDate = metric.first_session_date || date;

        if (isAwaiting) {
          return (
            <Stack>
              <Typography variant="body2" color="error" fontWeight={600}>
                Awaiting first update
              </Typography>
              {firstSessionDate && (
                <Typography variant="caption" color="text.secondary">
                  Started {formatDistance(firstSessionDate, new Date(), { addSuffix: true })}
                </Typography>
              )}
            </Stack>
          );
        }

        return (
          <Stack>
            <Typography variant="body2">
              {date.toLocaleDateString()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDistance(date, new Date(), { addSuffix: true })}
            </Typography>
          </Stack>
        );
      },
    },
    {
      field: 'achievement',
      headerName: 'Achievement',
      flex: 1.2,
      minWidth: 160,
      renderCell: (params: GridRenderCellParams) => {
        const progress = calculateAchievementProgress(params.row);
        const getColor = () => {
          if (progress === 100) return theme.palette.success.main;
          if (progress >= 50) return theme.palette.warning.main;
          return theme.palette.error.main;
        };
        
        return (
          <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: alpha(getColor(), 0.1),
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: getColor(),
                    borderRadius: 4,
                  }
                }}
              />
            </Box>
            <Typography variant="caption" fontWeight={600} sx={{ minWidth: 35, color: getColor() }}>
              {Math.round(progress)}%
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'summary',
      headerName: 'Summary',
      flex: 2,
      minWidth: 300,
      renderCell: (params: GridRenderCellParams) => {
        const metric = params.row;
        if (metric.awaiting_first_update) {
          const firstSessionDate = metric.first_session_date as Date | undefined;
          return (
            <Typography variant="body2" color="error" fontWeight={600}>
              Awaiting first success update{firstSessionDate ? ` - started ${firstSessionDate.toLocaleDateString()}` : ''}
            </Typography>
          );
        }

        const items = [];
        
        if (metric.goal_update && metric.goal_update.trim()) items.push(`Goal: ${metric.goal_update}`);
        if (metric.target1_update && metric.target1_update.trim()) items.push(`T1: ${metric.target1_update}`);
        if (metric.target2_update && metric.target2_update.trim()) items.push(`T2: ${metric.target2_update}`);
        if (metric.target3_update && metric.target3_update.trim()) items.push(`T3: ${metric.target3_update}`);
        if (metric.comments && metric.comments.trim() && !metric.comments.includes('Auto-created')) {
          items.push(`Comments: ${metric.comments}`);
        }
        
        const summary = items.join(' | ');
        
        return (
          <Tooltip title={summary || 'Update details'} arrow>
            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {summary}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
          <IconButton
            size="small"
            onClick={() => handleRowClick(params.row.client_id)}
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.2),
              },
            }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </motion.div>
      ),
    },
  ];

  if (!user || !userMetadata || (userMetadata.role !== 'ADMIN' && userMetadata.role !== 'MANAGER')) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          You don't have permission to access this page.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Client Success Overview
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Track client progress and achievements
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <AnimatePresence>
              {activeFilters > 0 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.2 }}
                >
                  <Chip
                    label={`${activeFilters} filter${activeFilters > 1 ? 's' : ''} active`}
                    onDelete={clearFilters}
                    deleteIcon={<ClearIcon />}
                    color="primary"
                    variant="outlined"
                  />
                </motion.div>
              )}
            </AnimatePresence>
            <IconButton
              onClick={handleRefresh}
              disabled={isRefreshing}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                },
              }}
            >
              <RefreshIcon className={isRefreshing ? 'rotating' : ''} />
            </IconButton>
          </Stack>
        </Stack>
      </motion.div>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Box>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {[1, 2, 3, 4].map((i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 3 }} />
              </Grid>
            ))}
          </Grid>
          <Skeleton variant="rectangular" height={60} sx={{ mb: 3, borderRadius: 2 }} />
          <LoadingContainer loading={true} minHeight="400px" message="LOADING SUCCESS DATA" />
        </Box>
      ) : (
        <>
          {renderStatistics()}

          {/* Enhanced Filters */}
          <MotionCard
            sx={{ mb: 3, overflow: 'visible' }}
            elevation={0}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" mb={2}>
                <FilterListIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="h6" fontWeight={600}>
                  Filters
                </Typography>
              </Stack>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Search Clients"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      sx: {
                        borderRadius: 2,
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          },
                        },
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Location</InputLabel>
                    <Select
                      value={locationFilter}
                      onChange={(e) => setLocationFilter(e.target.value)}
                      label="Location"
                      startAdornment={
                        <InputAdornment position="start">
                          <LocationIcon />
                        </InputAdornment>
                      }
                      sx={{
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': {
                          transition: 'all 0.2s',
                        },
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          },
                        },
                      }}
                    >
                      <MenuItem value="">All Locations</MenuItem>
                      {locations.map((location) => (
                        <MenuItem key={location} value={location}>
                          {location}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Coach</InputLabel>
                    <Select
                      value={coachFilter}
                      onChange={(e) => setCoachFilter(e.target.value)}
                      label="Coach"
                      startAdornment={
                        <InputAdornment position="start">
                          <PersonIcon />
                        </InputAdornment>
                      }
                      sx={{
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': {
                          transition: 'all 0.2s',
                        },
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          },
                        },
                      }}
                    >
                      <MenuItem value="">All Coaches</MenuItem>
                      {coaches.map((coach) => (
                        <MenuItem key={coach} value={coach}>
                          {formatName(coach)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Achievement</InputLabel>
                    <Select
                      value={achievementFilter}
                      onChange={(e) => setAchievementFilter(e.target.value)}
                      label="Achievement"
                      startAdornment={
                        <InputAdornment position="start">
                          <CheckCircleIcon />
                        </InputAdornment>
                      }
                      sx={{
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': {
                          transition: 'all 0.2s',
                        },
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          },
                        },
                      }}
                    >
                      <MenuItem value="">All Levels</MenuItem>
                      <MenuItem value="all">100% Complete</MenuItem>
                      <MenuItem value="partial">Partial</MenuItem>
                      <MenuItem value="none">Not Started</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </MotionCard>

          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              aria-label="success overview tabs"
              sx={{ 
                '& .MuiTab-root': {
                  fontWeight: 600,
                  fontSize: '1rem',
                }
              }}
            >
              <Tab label="Recent Updates" />
              <Tab 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Overdue Updates
                    {overdueUpdates.length > 0 && (
                      <Chip 
                        label={overdueUpdates.length} 
                        color="error" 
                        size="small" 
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Box>
                } 
              />
            </Tabs>
          </Box>

          {/* Recent Updates Tab */}
          <TabPanel value={tabValue} index={0}>
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Paper
                elevation={0}
                sx={{
                  height: 600,
                  borderRadius: 3,
                  overflow: 'hidden',
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <DataGrid
                  rows={filteredRecentUpdates}
                  columns={columns}
                  pageSizeOptions={[10, 25, 50]}
                  disableRowSelectionOnClick
                  density="comfortable"
                  onRowClick={(params) => handleRowClick(params.row.client_id)}
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-columnHeaders': {
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                      borderBottom: `2px solid ${theme.palette.divider}`,
                      '& .MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      },
                    },
                    '& .MuiDataGrid-cell': {
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    },
                    '& .MuiDataGrid-row': {
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.02),
                        cursor: 'pointer',
                      },
                    },
                  }}
                  slots={{
                    loadingOverlay: () => (
                      <Stack height="100%" alignItems="center" justifyContent="center">
                        <CustomLoader size={50} message="LOADING" />
                      </Stack>
                    ),
                    noRowsOverlay: () => (
                      <Stack height="100%" alignItems="center" justifyContent="center">
                        <Typography variant="body1" color="text.secondary">
                          No recent updates found
                        </Typography>
                      </Stack>
                    ),
                  }}
                />
              </Paper>
            </MotionBox>
          </TabPanel>

          {/* Overdue Updates Tab */}
          <TabPanel value={tabValue} index={1}>
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {/* Section 1: Awaiting First Update */}
              <Box mb={4}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  mb={2}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.08)} 0%, ${alpha(theme.palette.warning.main, 0.03)} 100%)`,
                  }}
                >
                  <WarningIcon sx={{ color: theme.palette.warning.main, fontSize: 28 }} />
                  <Typography variant="h6" fontWeight={700} sx={{ flexGrow: 1 }}>
                    Awaiting First Update
                  </Typography>
                  <Chip
                    label={awaitingFirstUpdate.length}
                    color="warning"
                    size="small"
                    sx={{ fontWeight: 700, minWidth: 40 }}
                  />
                </Stack>
                <Paper
                  elevation={0}
                  sx={{
                    height: 400,
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: `2px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                    background: alpha(theme.palette.warning.main, 0.02),
                  }}
                >
                  <DataGrid
                    rows={awaitingFirstUpdate}
                    columns={columnsForFirstUpdate}
                    pageSizeOptions={[5, 10, 25]}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 5 } },
                    }}
                    disableRowSelectionOnClick
                    density="comfortable"
                    onRowClick={(params) => handleRowClick(params.row.client_id)}
                    sx={{
                      border: 'none',
                      '& .MuiDataGrid-columnHeaders': {
                        bgcolor: alpha(theme.palette.warning.main, 0.08),
                        borderBottom: `2px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                        '& .MuiDataGrid-columnHeaderTitle': {
                          fontWeight: 700,
                          fontSize: '0.875rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          color: theme.palette.warning.dark,
                        },
                      },
                      '& .MuiDataGrid-cell': {
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      },
                      '& .MuiDataGrid-row': {
                        '&:hover': {
                          bgcolor: alpha(theme.palette.warning.main, 0.05),
                          cursor: 'pointer',
                        },
                      },
                    }}
                    slots={{
                      loadingOverlay: () => (
                        <Stack height="100%" alignItems="center" justifyContent="center">
                          <CustomLoader size={50} message="LOADING" />
                        </Stack>
                      ),
                      noRowsOverlay: () => (
                        <Stack height="100%" alignItems="center" justifyContent="center" spacing={1}>
                          <CheckCircleIcon sx={{ fontSize: 48, color: theme.palette.success.main, opacity: 0.5 }} />
                          <Typography variant="body1" color="text.secondary" fontWeight={600}>
                            All new clients have received their first update
                          </Typography>
                        </Stack>
                      ),
                    }}
                  />
                </Paper>
              </Box>

              {/* Section 2: Overdue Updates (with history) */}
              <Box>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  mb={2}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)} 0%, ${alpha(theme.palette.error.main, 0.03)} 100%)`,
                  }}
                >
                  <WarningIcon sx={{ color: theme.palette.error.main, fontSize: 28 }} />
                  <Typography variant="h6" fontWeight={700} sx={{ flexGrow: 1 }}>
                    Overdue Updates
                  </Typography>
                  <Chip
                    label={overdueWithHistory.length}
                    color="error"
                    size="small"
                    sx={{ fontWeight: 700, minWidth: 40 }}
                  />
                </Stack>
                <Paper
                  elevation={0}
                  sx={{
                    height: 400,
                    borderRadius: 3,
                    overflow: 'hidden',
                    border: `2px solid ${alpha(theme.palette.error.main, 0.3)}`,
                    background: alpha(theme.palette.error.main, 0.02),
                  }}
                >
                  <DataGrid
                    rows={overdueWithHistory}
                    columns={columns}
                    pageSizeOptions={[5, 10, 25]}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 5 } },
                    }}
                    disableRowSelectionOnClick
                    density="comfortable"
                    onRowClick={(params) => handleRowClick(params.row.client_id)}
                    sx={{
                      border: 'none',
                      '& .MuiDataGrid-columnHeaders': {
                        bgcolor: alpha(theme.palette.error.main, 0.08),
                        borderBottom: `2px solid ${alpha(theme.palette.error.main, 0.2)}`,
                        '& .MuiDataGrid-columnHeaderTitle': {
                          fontWeight: 700,
                          fontSize: '0.875rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          color: theme.palette.error.dark,
                        },
                      },
                      '& .MuiDataGrid-cell': {
                        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      },
                      '& .MuiDataGrid-row': {
                        '&:hover': {
                          bgcolor: alpha(theme.palette.error.main, 0.05),
                          cursor: 'pointer',
                        },
                      },
                    }}
                    slots={{
                      loadingOverlay: () => (
                        <Stack height="100%" alignItems="center" justifyContent="center">
                          <CustomLoader size={50} message="LOADING" />
                        </Stack>
                      ),
                      noRowsOverlay: () => (
                        <Stack height="100%" alignItems="center" justifyContent="center" spacing={1}>
                          <CheckCircleIcon sx={{ fontSize: 48, color: theme.palette.success.main, opacity: 0.5 }} />
                          <Typography variant="body1" color="text.secondary" fontWeight={600}>
                            Great! No overdue updates
                          </Typography>
                        </Stack>
                      ),
                    }}
                  />
                </Paper>
              </Box>
            </MotionBox>
          </TabPanel>
        </>
      )}

      <style>{`
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .rotating {
          animation: rotate 1s linear infinite;
        }
      `}</style>
    </Container>
  );
};

export default SuccessOverviewOptimized;
